import {
  type JSX,
  type ReactNode,
  createContext,
  use,
  useCallback,
  useMemo,
  useState
} from 'react'

import { readTimeZone, writeTimeZone } from '../lib/time-zone-store.ts'
import { parseTimeZone, UTC_TIME_ZONE } from '../lib/time-zone.ts'

/** What `useTimeZone` hands a consumer. */
export interface TimeZoneContextValue {
  /** The zone in force. An unset choice is UTC. */
  timeZone: string
  /** Hold a zone, and apply it to this tab. */
  setTimeZone: (timeZone: string) => void
}

const TimeZoneContext = createContext<TimeZoneContextValue>({
  timeZone: UTC_TIME_ZONE,
  setTimeZone: writeTimeZone
})

/**
 * Read the reader's zone, and change it.
 *
 * Outside a provider this is UTC, and a write still persists.
 * A screen test that renders no provider keeps working. The rail sets the
 * zone through the provider, so a change re-renders every report.
 *
 * @returns The zone context.
 */
export function useTimeZone(): TimeZoneContextValue {
  return use(TimeZoneContext)
}

/**
 * TimeZoneProvider — the reader's zone for every report.
 *
 * Sits beside the theme provider, above both the signed-in app and the
 * sign-in screen, so signing in does not remount it and drop the choice.
 *
 * @param props - Provider props.
 * @param props.children - The app.
 * @returns The provider.
 */
export function TimeZoneProvider({
  children
}: {
  children: ReactNode
}): JSX.Element {
  const [timeZone, setHeld] = useState(() => readTimeZone() ?? UTC_TIME_ZONE)
  const setTimeZone = useCallback((next: string) => {
    const zone = parseTimeZone(next)
    if (zone === null) {
      return
    }
    writeTimeZone(zone)
    setHeld(zone)
  }, [])
  const value = useMemo(
    () => ({ timeZone, setTimeZone }),
    [timeZone, setTimeZone]
  )

  return <TimeZoneContext value={value}>{children}</TimeZoneContext>
}
